/**
 * The part of the exploded view that needs a 3D library.
 *
 * `@shared/lib/cad/explode` owns the rules and returns a *world-space* offset. A part node's
 * `position`, though, is expressed in its parent's local frame, so the offset has to be rotated
 * (and unscaled) into that frame before it can be added. Skipping this step is the classic bug
 * behind parts that fly off in the wrong direction as soon as an assembly has a rotated sub-group.
 */

import {Box3, Matrix3, Object3D, Vector3} from 'three';

import {Vec3} from '@shared/models/cad_model';

/** Reused across calls: this runs for every part on every animation frame. */
const scratchMatrix = new Matrix3();
const scratchVector = new Vector3();

/**
 * Converts a world-space displacement into the local frame of `node`'s parent.
 *
 * Only the linear part of the parent's world matrix is used, which is what makes this a
 * *direction* transform: translation must not apply to a displacement.
 */
export function worldOffsetToLocal(node: Object3D, worldOffset: Vec3): Vector3 {
  const parent = node.parent;
  scratchVector.set(worldOffset[0], worldOffset[1], worldOffset[2]);
  if (parent === null) {
    return scratchVector.clone();
  }
  parent.updateWorldMatrix(true, false);
  scratchMatrix.setFromMatrix4(parent.matrixWorld).invert();
  return scratchVector.applyMatrix3(scratchMatrix).clone();
}

/** World-space centre of a node's bounding box, including all of its descendants. */
export function worldCenterOf(node: Object3D, target?: Box3): Vector3 {
  const box = (target ?? new Box3()).setFromObject(node, true);
  const center = new Vector3();
  if (box.isEmpty()) {
    return node.getWorldPosition(center);
  }
  return box.getCenter(center);
}

export function toVec3(vector: Vector3): Vec3 {
  return [vector.x, vector.y, vector.z];
}
